import React, { useState } from 'react';
import { Star } from 'lucide-react';

/**
 * StarRating — reusable 5-star rating component.
 *
 * Props:
 *   rating: number (0-5) — current rating to display
 *   count: number — number of ratings (shown as "(N ratings)")
 *   interactive: boolean — if true, stars are clickable
 *   onRate: function(score) — callback when a star is clicked
 *   size: "sm" | "md" | "lg" — star size
 *   showCount: boolean — whether to show the count label
 */
function StarRating({ rating = 0, count = 0, interactive = false, onRate, size = "sm", showCount = true }) {
  const [hoverRating, setHoverRating] = useState(0);

  const sizeMap = { sm: 14, md: 18, lg: 24 };
  const starSize = sizeMap[size] || 14;

  const displayRating = hoverRating || rating;

  return (
    <div className="star-rating" style={{ display: 'inline-flex', alignItems: 'center', gap: '2px' }}>
      {[1, 2, 3, 4, 5].map((star) => {
        const filled = star <= Math.round(displayRating);
        return (
          <Star
            key={star}
            className={`star-icon ${filled ? 'star-filled' : 'star-empty'}`}
            style={{
              width: starSize,
              height: starSize,
              cursor: interactive ? 'pointer' : 'default',
              fill: filled ? '#f59e0b' : 'none',
              stroke: filled ? '#f59e0b' : '#d1d5db',
              strokeWidth: 1.5,
              transition: 'all 0.15s ease',
            }}
            onClick={() => interactive && onRate && onRate(star)}
            onMouseEnter={() => interactive && setHoverRating(star)}
            onMouseLeave={() => interactive && setHoverRating(0)}
          />
        );
      })}
      {showCount && count > 0 && (
        <span style={{
          marginLeft: '4px',
          fontSize: size === 'lg' ? '0.9rem' : '0.75rem',
          color: '#6B7280',
        }}>
          {rating > 0 ? rating.toFixed(1) : ''} ({count})
        </span>
      )}
      {showCount && count === 0 && (
        <span style={{
          marginLeft: '4px',
          fontSize: size === 'lg' ? '0.9rem' : '0.75rem',
          color: '#9CA3AF',
          fontStyle: 'italic',
        }}>
          No ratings
        </span>
      )}
    </div>
  );
}

export default StarRating;
